/**
 * /settings/calendar — a coach connects Google Calendar through Composio,
 * chooses the calendar DCM adds events to and the calendars that count as
 * busy, confirms her time zone, and can disconnect or reconnect. Only for
 * coaches with the `calendar_connection` flag while Composio is configured;
 * everyone else is sent to Settings.
 *
 * Aliases: calendar settings page, connect calendar page
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { TimeZoneCard } from "~/components/time-zone-card";
import { ownTimeZoneQueryOptions } from "~/lib/queries/accountSettingsQuery";
import {
  calendarSettingsQueryOptions,
  calendarSummaryQueryOptions,
} from "~/lib/queries/calendarQuery";
import { CALENDAR_RETURN_PARAM } from "~/lib/shared/calendar";
import { createNoIndexMeta } from "~/lib/shared/meta";

import { CalendarConnectionCard } from "./-components/calendar-connection-card";

export const Route = createFileRoute("/_user/settings/calendar/")({
  staticData: { title: "Calendar" },
  validateSearch: z.object({
    [CALENDAR_RETURN_PARAM]: z.string().optional(),
  }),
  beforeLoad: async ({ context }) => {
    const summary = await context.queryClient
      .fetchQuery(calendarSummaryQueryOptions())
      .catch(() => null);
    if (!summary?.available) throw redirect({ to: "/settings" });
  },
  head: () =>
    createNoIndexMeta(
      "Calendar settings – Dream Coach Match",
      "Connect your Google Calendar to Dream Coach Match.",
    ),
  // Hands SSR the page's data; each card reads its own query and shows its
  // own failure, so a failed read here never fails the page.
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient
        .ensureQueryData(calendarSettingsQueryOptions())
        .catch(() => undefined),
      context.queryClient
        .ensureQueryData(ownTimeZoneQueryOptions())
        .catch(() => undefined),
    ]);
  },
  component: CalendarSettingsPage,
});

function CalendarSettingsPage(): React.JSX.Element {
  const search = Route.useSearch();
  const { data: settings } = useQuery(calendarSettingsQueryOptions());
  return (
    <div className="max-w-4xl space-y-4">
      <CalendarConnectionCard
        returned={search[CALENDAR_RETURN_PARAM] === "returned"}
      />
      {settings?.state === "connected" && (
        <TimeZoneCard calendarZone={settings.calendarTimeZone} />
      )}
    </div>
  );
}
